import React, { createContext, useState, useContext, useEffect } from 'react';
import { adminLogin, verifyAdminToken, adminLogout } from '../services/api';

const AdminContext = createContext();

export const useAdmin = () => useContext(AdminContext);

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));

  useEffect(() => {
    if (token) {
      verifyAdmin();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyAdmin = async () => {
    try {
      const response = await verifyAdminToken();
      if (response.valid && response.admin) {
        setAdmin(response.admin);
      } else {
        localStorage.removeItem('adminToken');
        setToken(null);
      }
    } catch (error) {
      localStorage.removeItem('adminToken');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await adminLogin(email, password);
      if (response.success) {
        const newToken = response.token;
        localStorage.setItem('adminToken', newToken);
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }
        setToken(newToken);
        setAdmin(response.admin);
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (error) {
      return { success: false, message: error.message || 'Login failed' };
    }
  };

  const logout = async () => {
    try {
      await adminLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('rememberMe');
      setToken(null);
      setAdmin(null);
      window.location.href = '/admin/login';
    }
  };

  const hasPermission = (permission) => {
    if (!admin) return false;
    if (admin.role === 'super_admin') return true;
    return admin.permissions?.includes(permission) || false;
  };

  return (
    <AdminContext.Provider value={{ 
      admin, 
      loading, 
      login, 
      logout, 
      token,
      hasPermission,
      isAuthenticated: !!admin 
    }}>
      {children}
    </AdminContext.Provider>
  );
};